import { Injectable, inject, signal } from '@angular/core';
import { catchError, of, Observable, map } from 'rxjs';
import { Router } from '@angular/router';
import { LoginService, ProductService, UserService } from '../../api/services';
import { LoginModel, TokenDto, UserDto } from '../../api/models';
import { StoreService, StoreKeysEnum } from '../core/store.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	#loginService = inject(LoginService);
	#router = inject(Router);
	#userService = inject(UserService);
	#productService = inject(ProductService);
	#store = inject(StoreService);

	isAuthenticated = signal<boolean>(false);
	user = signal<UserDto>({ id: '' });
	lastProductPath = signal<string>('');
	editorMenuIds = signal<string[]>([]);

	constructor() {
		this.updateIsAuthenticated();
	}

	login(email: string, password: string): Observable<boolean> {
		const params: LoginModel = { email, password };

		return this.#loginService.loginLogin$Json({ body: params }).pipe(
			catchError((err) => {
				console.error('Login failed', err);
				this.#authenticate(false);
				return of(null);
			}),
			map((res: TokenDto | null) => {
				if (res?.token) {
					this.#store.set(StoreKeysEnum.AUTH, res.token);
					this.#authenticate(true);
					this.updateUser();
					return true;
				}
				console.error('Login failed');
				return false;
			}),
		);
	}

	logout() {
		localStorage.removeItem(StoreKeysEnum.AUTH);
		this.#router.navigate([this.lastProductPath()]);
		this.updateIsAuthenticated();
	}

	getToken(): string | null {
		return this.#store.get(StoreKeysEnum.AUTH);
	}

	updateIsAuthenticated() {
		const token = this.getToken();

		if (!token) {
			this.#authenticate(false);
			return;
		}

		this.#loginService
			.loginValidateAndRefreshToken$Json({ body: { token } })
			.subscribe({
				next: (refreshedToken) => {
					this.#authenticate(refreshedToken.length > 0);
					this.#store.set(StoreKeysEnum.AUTH, refreshedToken);
					this.updateUser();
				},
				error: (err) => {
					console.error('Token validation failed', err);
					this.#authenticate(false);
				},
			});
	}

	updateUser() {
		this.#userService.userGetUser$Json().subscribe((res) => {
			this.user.set(res);
			this.updateEditorMenus();
		});
	}

	updateEditorMenus() {
		if (this.isAuthenticated()) {
			this.#productService
				.productGetAllEditorMenus$Json()
				.subscribe((menus) => {
					this.editorMenuIds.set(menus.map((m) => m.id ?? ''));
				});
		} else {
			this.editorMenuIds.set([]);
		}
	}

	hasEditorAccess(menuId: string): boolean {
		return this.editorMenuIds().includes(menuId);
	}

	redirectToRequestPath() {
		this.#router.navigate([
			this.#store.get(StoreKeysEnum.REQUEST_PATH) ?? '/demo',
		]);
	}

	setRequestPath(path: string) {
		this.#store.set(StoreKeysEnum.REQUEST_PATH, path);

		if (this.#isPublicPath(path)) {
			this.lastProductPath.set(path);
		}
	}

	#isPublicPath(path: string): boolean {
		return path.startsWith('/product');
	}

	#authenticate(isAuthenticated: boolean) {
		this.isAuthenticated.set(isAuthenticated);
	}
}
